<script setup>
import { checkLikeComment, patchCommentLike, getCommentCount } from '@/api/boardCommentApi'
import IconCommentLike from '../svg/IconCommentLike.vue'
import { DEV_MODE } from '@/config/dev-mode'
import Pagination from './Pagination.vue'
import SimplePagination from './SimplePagination.vue'

const { isMobile } = useDeviceDetect()

const props = defineProps({
  comments: {
    type: Array,
    default: [],
  },
  currentUserId: {
    type: [String, Number],
    default: '',
  },
  articleId: {
    type: [Number, null],
    default: null,
  },
  currentPage: {
    type: Number,
    default: 1,
  },
  articleHasComment: {
    type: Boolean,
    default: false,
  },
})

const emit = defineEmits('openActions', 'onNestedReplyClick', 'openReport', 'page-changed')

// 댓글 유무
const hasNoComments = computed(() => !props.comments || props.comments.length === 0)

// 댓글 좋아요
const likedById = reactive({})
/** 좋아요 등록 및 삭제 */
const patchLike = async (comment) => {
  try {
    const result = await patchCommentLike(comment.commentId)
    likedById[comment.commentId] = !!result.likeClicked
    comment.likeCount =
      result.likeCount ?? comment.likeCount + (likedById[comment.commentId] ? 1 : -1)
  } catch (e) {
    DEV_MODE && console.error(e)
  }
}

/** 좋아요 클릭 확인 */
// 백엔드에서 댓글 반환값 내부에 넣어주면 따로 호출하지 않아도 됨. 현재는 해당 API호출 필요
const checkLike = async (commentId) => {
  try {
    const result = await checkLikeComment(commentId)
  } catch (error) {
    DEV_MODE && console.error('좋아요 체크 오류', error)
  }
}

/** 내 댓글 여부 */
const isMine = (c) => String(c.userId) === String(props.currentUserId)

/** 날짜 변환 */
const toDotYMD = (isoLike) => {
  if (!isoLike) return ''
  const date = new Date(isoLike)
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  const hours = String(date.getHours()).padStart(2, '0')
  const minutes = String(date.getMinutes()).padStart(2, '0')
  const seconds = String(date.getSeconds()).padStart(2, '0')
  return `${year}.${month}.${day} ${hours}:${minutes}:${seconds}`
}

/** 옵션 메뉴 클릭시 */
const openOptionMenu = (comment) => {
  if (isMine(comment)) {
    emit('openActions', comment)
  } else {
    emit('openReport', comment)
  }
}

/** 대댓글 */
const addNestedReply = (comment) => {
  emit('onNestedReplyClick', comment)
}

// 페이징
const page = ref(1)
const PAGE_SIZE = 10
const totalCount = ref(0)
const totalPages = computed(() => {
  const total = totalCount.value || 0
  return total > 0 ? Math.ceil(total / PAGE_SIZE) : 0
})
// 서버 페이징이므로 그대로 렌더
const pagedComments = computed(() => props.comments || [])
const pageChanged = (p) => {
  page.value = p
  // 서버 재조회 필요하므로 상위에 이벤트 전달
  emit('page-changed', p)
}

// props.currentPage와 동기화
watch(
  () => props.currentPage,
  (newPage) => {
    // ref 객체인 경우 .value를 사용, 일반 숫자인 경우 그대로 사용
    page.value =
      typeof newPage === 'object' && newPage?.value !== undefined ? newPage.value : newPage || 1
  },
  { immediate: true },
)

// 댓글 목록 길이가 바뀌어도 현재 페이지는 유지

const refreshCommentCount = async () => {
  if (!props.articleId) return
  try {
    const cnt = await getCommentCount(props.articleId, 'ARTICLE')
    totalCount.value = Number(cnt) || 0
    // 댓글 수만 업데이트하고 페이지 이동은 하지 않음
  } catch (e) {
    DEV_MODE && console.error('getCommentCount 오류', e)
    totalCount.value = 0
  }
}

watch(
  () => props.articleId,
  () => {
    page.value = 1
    refreshCommentCount()
  },
  { immediate: true },
)
</script>

<template>
  <div class="board-comment__wrapper" v-if="articleHasComment === true">
    <!-- 댓글 없을 때 -->
    <div v-if="hasNoComments && isMobile" class="board-comment__box none-reply">
      <div class="board-comment__none-wrapper">
        <div class="none-reply__icon"></div>
        <span class="none-reply__txt">
          등록된 댓글이 아직 없습니다.<br />
          첫번째 댓글을 남겨주세요!
        </span>
      </div>
    </div>

    <!-- 댓글 있을 때 -->
    <div
      v-else
      v-for="comment in pagedComments"
      :key="comment.commentId"
      class="board-comment__box"
      :class="{ 'nested-reply': comment.upperCommentId }"
    >
      <template v-if="!isMine(comment) && comment.isPrivate">
        <div class="board-comment__body secret">
          <div class="board-comment__top">
            <div class="board-comment__top-info">
              <span class="comment-info__name">
                <span>{{ comment.nickname || '낭만회원' }}</span>
                <i class="secret-comment-name" v-if="comment.isPrivate"></i
              ></span>
              <span class="comment-info__date">{{ toDotYMD(comment.updatedAt) }}</span>
            </div>
            <div class="board-comment__top-menu" @click="openOptionMenu(comment)"></div>
          </div>
          <div class="board-comment__body">
            <span class="board-comment__body-content">비밀댓글입니다.</span>
          </div>
        </div>
      </template>
      <template v-else>
        <div class="board-comment__top">
          <div class="board-comment__top-info">
            <span class="comment-info__name">
              <span>{{ comment.nickname || '낭만회원' }}</span>
              <i class="secret-comment-name" v-if="comment.isPrivate"></i
            ></span>
            <span class="comment-info__date">{{ toDotYMD(comment.updatedAt) }}</span>
          </div>
          <div class="board-comment__top-menu" @click="openOptionMenu(comment)"></div>
        </div>

        <div class="board-comment__body">
          <span class="board-comment__body-content">
            {{ comment.name }}
          </span>
        </div>

        <div class="board-comment__bottom">
          <div
            v-if="currentUserId"
            class="board-commetn__nested-reply"
            @click="addNestedReply(comment)"
          >
            <span v-if="!comment.upperCommentId">답글 작성</span>
          </div>
          <div
            class="board-comment__like-btn"
            :aria-pressed="!!likedById[comment.commentId]"
            @click="patchLike(comment)"
          >
            <IconCommentLike :active="!!likedById[comment.commentId]" />
            <span class="comment__like-count">{{ comment.likeCount }}</span>
          </div>
        </div>
        <!-- 첫 댓글로 이동 버튼을 위한 공간 -->
        <div class="comment-action-space"></div>
      </template>
    </div>
  </div>
  <!-- PC 버전 페이지네이션 -->
  <Pagination
    v-if="totalPages > 0 && !isMobile"
    :total-pages="Number(totalPages) || 0"
    :current-page="Number(currentPage) || 1"
    :max-visible-pages="10"
    @page-changed="pageChanged"
  />

  <!-- 모바일 버전 간단한 페이지네이션 -->
  <SimplePagination
    v-if="totalPages > 0 && isMobile"
    :total-pages="Number(totalPages) || 0"
    :current-page="Number(page) || 1"
    @page-changed="pageChanged"
  />
</template>
<style lang="scss">
.nested-reply {
  padding-left: 47px !important;
  position: relative;

  &::before {
    content: '↳';
    position: absolute;
    left: 27px;
    top: 24px;
    color: var(--Gray-400, #bdbdbd);
    font-size: 12px;
    line-height: 1;
  }
}

.board-comment__body-content {
  word-break: break-all;
  word-wrap: break-word;
  overflow-wrap: break-word;
  hyphens: auto;
}

.comment-action-space {
  height: 0;
  margin-bottom: 0;
}
</style>
